import { applyDecoratedDescriptor as _applyDecoratedDescriptor } from '../../_virtual/_rollupPluginBabelHelpers.mjs';
import _en from './translations/en.json.mjs';
import React from 'react';
import isEqual from 'lodash/isEqual';
import { Select, TextField, Button, Icon, Modal, FormLayout, Link, InlineStack, RadioButton, Checkbox } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { autobind } from '../../packages/@web-utilities/decorators/index.mjs';
import { ImageSize, Alignment } from '../../types/index.mjs';
import styles from './EditImageModal.scss.mjs';
import { supportedLanguageFromLocale } from '../../locales/utilities.mjs';
import RteLeft from './icons/rte-left.svg.mjs';
import RteCenter from './icons/rte-center.svg.mjs';
import RteRight from './icons/rte-right.svg.mjs';
import { getImageSizeFromUrl, getResizedImage, getSizedImage } from '../../packages/@web-utilities/url/utilities.mjs';
import compose from '@shopify/react-compose';

var _class;
const SHOPIFY_CDN_REGEX = /files\/\d\/(\d{4}\/){2,}(files|products|assets)/;
// eslint-disable-next-line react/no-unsafe
let EditImageModal = (_class = class EditImageModal extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = getStateFromProps(this.props);
    this.imageSizeOptions = [{
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.original'),
      value: ImageSize.Original
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.inline'),
      value: ImageSize.Inline
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.icon'),
      value: ImageSize.Icon
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.thumb'),
      value: ImageSize.Thumb
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.smallLogo'),
      value: ImageSize.SmallLogo
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.logo'),
      value: ImageSize.Logo
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.productThumb'),
      value: ImageSize.ProductThumb
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.productImage'),
      value: ImageSize.ProductImage
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.bannerImage'),
      value: ImageSize.BannerImage
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.wallpaper1024'),
      value: ImageSize.WallPaper1024
    }, {
      label: this.props.i18n.translate('RichTextEditor.imageSizeOptions.wallpaper2048'),
      value: ImageSize.WallPaper2048
    }];
  }
  UNSAFE_componentWillReceiveProps(newProps) {
    if (!isEqual(newProps, this.props)) {
      this.setState(getStateFromProps(newProps));
    }
  }
  render() {
    const {
      i18n,
      open,
      onClose,
      onRemove
    } = this.props;
    const {
      isShopifyUrl,
      imageSize,
      imageAltText,
      alignment,
      wrapText,
      spacing,
      imageUrl
    } = this.state;
    const shopifyUrlMarkup = isShopifyUrl ? /*#__PURE__*/React.createElement(Select, {
      label: i18n.translate('EditImageModal.imageSelectLabel'),
      options: this.imageSizeOptions,
      value: imageSize,
      onChange: this.handleImageSizeChange
    }) : /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('EditImageModal.imageUrlLabel'),
      value: imageUrl,
      onChange: this.handleImageUrlChange
    });
    const removeImageButtonMarkup = /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      tone: "critical",
      onClick: onRemove
    }, i18n.translate('EditImageModal.removeImage'));
    const leftAlignmentIcon = /*#__PURE__*/React.createElement(Icon, {
      source: RteLeft,
      accessibilityLabel: i18n.translate('RichTextEditor.alignments.left')
    });
    const centerAlignmentIcon = /*#__PURE__*/React.createElement(Icon, {
      source: RteCenter,
      accessibilityLabel: i18n.translate('RichTextEditor.alignments.center')
    });
    const rightAlignmentIcon = /*#__PURE__*/React.createElement(Icon, {
      source: RteRight,
      accessibilityLabel: i18n.translate('RichTextEditor.alignments.right')
    });

    // replacement for helpCenterUrl in @web-utilties/url from Shopify/web
    const getHelpCenterUrl = () => {
      const language = supportedLanguageFromLocale(i18n.locale);
      return `https://help.shopify.com/${language}/manual/products/product-media/add-alt-text`;
    };
    return /*#__PURE__*/React.createElement(Modal, {
      title: i18n.translate('RichTextEditor.editImage'),
      open: open,
      onClose: onClose,
      primaryAction: {
        content: i18n.translate('RichTextEditor.editImage'),
        onAction: this.handleEditImage
      },
      secondaryActions: [{
        content: i18n.translate('RichTextEditor.cancel'),
        onAction: onClose
      }],
      footer: removeImageButtonMarkup,
      sectioned: true
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.Container
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.ImageInfo
    }, /*#__PURE__*/React.createElement(FormLayout, null, shopifyUrlMarkup, /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('RichTextEditor.altTextLabel'),
      value: imageAltText,
      onChange: this.handleImageAltTextChange,
      helpText: i18n.translate('RichTextEditor.altTextHelpText', {
        altTextLink: /*#__PURE__*/React.createElement(Link, {
          external: true,
          url: getHelpCenterUrl()
        }, i18n.translate('RichTextEditor.altText'))
      })
    }), /*#__PURE__*/React.createElement("fieldset", {
      className: styles.AlignmentList
    }, /*#__PURE__*/React.createElement("legend", {
      className: styles.AlignmentTitle
    }, i18n.translate('EditImageModal.alignmentLabel')), /*#__PURE__*/React.createElement(InlineStack, null, /*#__PURE__*/React.createElement(RadioButton, {
      name: "ImageAlignment",
      id: Alignment.Left,
      checked: alignment === Alignment.Left,
      label: leftAlignmentIcon,
      onChange: this.handleAlignmentChange
    }), /*#__PURE__*/React.createElement(RadioButton, {
      name: "ImageAlignment",
      id: Alignment.Center,
      checked: alignment === Alignment.Center,
      label: centerAlignmentIcon,
      onChange: this.handleAlignmentChange
    }), /*#__PURE__*/React.createElement(RadioButton, {
      name: "ImageAlignment",
      id: Alignment.Right,
      checked: alignment === Alignment.Right,
      label: rightAlignmentIcon,
      onChange: this.handleAlignmentChange
    })), /*#__PURE__*/React.createElement(Checkbox, {
      label: i18n.translate('EditImageModal.wrapTextLabel'),
      checked: wrapText,
      disabled: alignment === Alignment.Center,
      onChange: this.handleWrapTextChange
    })))), /*#__PURE__*/React.createElement("div", {
      className: styles.SpacingBoxWrapper
    }, /*#__PURE__*/React.createElement("fieldset", {
      className: styles.SpacingBox
    }, /*#__PURE__*/React.createElement("legend", {
      className: styles.SpacingHeading
    }, i18n.translate('EditImageModal.spacing.title')), /*#__PURE__*/React.createElement("div", {
      className: styles.Field
    }, /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('EditImageModal.spacing.top'),
      value: spacing.top,
      onChange: this.handleTopSpacingChange
    })), /*#__PURE__*/React.createElement("div", {
      className: styles.Section
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.Field
    }, /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('EditImageModal.spacing.left'),
      value: spacing.left,
      onChange: this.handleLeftSpacingChange
    })), /*#__PURE__*/React.createElement("div", {
      className: styles.Field
    }, /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('EditImageModal.spacing.right'),
      value: spacing.right,
      onChange: this.handleRightSpacingChange
    }))), /*#__PURE__*/React.createElement("div", {
      className: styles.Field
    }, /*#__PURE__*/React.createElement(TextField, {
      autoComplete: "off",
      label: i18n.translate('EditImageModal.spacing.bottom'),
      value: spacing.bottom,
      onChange: this.handleBottomSpacingChange
    }))))));
  }
  handleImageSizeChange(newImageSize) {
    this.setState({
      imageSize: newImageSize
    });
  }
  handleImageAltTextChange(newImageAltText) {
    this.setState({
      imageAltText: newImageAltText
    });
  }
  handleImageUrlChange(newImageUrl) {
    this.setState({
      imageUrl: newImageUrl
    });
  }
  handleAlignmentChange(_, newAlignment) {
    if (newAlignment === Alignment.Center) {
      this.setState({
        alignment: newAlignment,
        wrapText: false
      });
    } else {
      this.setState({
        alignment: newAlignment
      });
    }
  }
  handleWrapTextChange(newWrapText) {
    this.setState({
      wrapText: newWrapText
    });
  }
  handleTopSpacingChange(newTopSpacing) {
    this.setState(prevState => {
      return {
        spacing: {
          ...prevState.spacing,
          top: newTopSpacing
        }
      };
    });
  }
  handleLeftSpacingChange(newLeftSpacing) {
    this.setState(prevState => {
      return {
        spacing: {
          ...prevState.spacing,
          left: newLeftSpacing
        }
      };
    });
  }
  handleRightSpacingChange(newRightSpacing) {
    this.setState(prevState => {
      return {
        spacing: {
          ...prevState.spacing,
          right: newRightSpacing
        }
      };
    });
  }
  handleBottomSpacingChange(newBottomSpacing) {
    this.setState(prevState => {
      return {
        spacing: {
          ...prevState.spacing,
          bottom: newBottomSpacing
        }
      };
    });
  }
  handleEditImage() {
    const {
      isShopifyUrl,
      imageAltText,
      imageUrl,
      imageSize,
      alignment,
      wrapText,
      spacing
    } = this.state;
    const newUrl = isShopifyUrl ? getResizedImageUrl(imageUrl, imageSize) : imageUrl;
    this.props.onEditImage(newUrl, imageAltText, alignment, wrapText, spacing);
  }
}, (_applyDecoratedDescriptor(_class.prototype, "handleImageSizeChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleImageSizeChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleImageAltTextChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleImageAltTextChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleImageUrlChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleImageUrlChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleAlignmentChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleAlignmentChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleWrapTextChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleWrapTextChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleTopSpacingChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleTopSpacingChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleLeftSpacingChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleLeftSpacingChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleRightSpacingChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleRightSpacingChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleBottomSpacingChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleBottomSpacingChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleEditImage", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditImage"), _class.prototype)), _class);
function getResizedImageUrl(url, imageSize) {
  return getImageSizeFromUrl(url) ? getResizedImage(url, imageSize) : getSizedImage(url, imageSize);
}
function getStateFromProps(props) {
  const {
    imageUrl,
    alignment,
    spacing,
    altText,
    wrapText
  } = props;
  return {
    isShopifyUrl: SHOPIFY_CDN_REGEX.test(imageUrl || ''),
    imageAltText: altText || '',
    wrapText: wrapText || false,
    imageUrl: imageUrl || '',
    alignment: alignment || Alignment.Left,
    spacing: spacing || {
      top: '',
      right: '',
      bottom: '',
      left: ''
    },
    imageSize: imageUrl == null ? ImageSize.ProductImage : getImageSizeFromUrl(imageUrl)
  };
}
var EditImageModal$1 = compose(withI18n({
  id: 'EditImageModal_vjy944',
  fallback: _en,
  translations(locale) {
    if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
      return;
    }
    return import( /* webpackChunkName: "EditImageModal_vjy944-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
  }
}))(EditImageModal);

export { EditImageModal$1 as default };
